<template>
  <v-card>
    <v-card-title>
      <v-row>
        <v-col v-if="company.contactCenterWorkflow" cols="12" sm="6">
          <!-- Filter for categories -->
          <v-select
            v-model="categoriesFilter"
            :items="categoriesList"
            label="Categories"
            multiple
            chips
            deletable-chips
            clearable
          />
        </v-col>
        <v-col
          sm="6"
          :offset-sm="company.contactCenterWorkflow ? 0 : 6"
          cols="12"
        >
          <div class="d-flex">
            <v-text-field
              v-model.trim="searchText"
              label="Search name, email, testator ID or ID number"
              single-line
              hide-details
              clearable
              class="pt-sm-5"
              @click:clear="clearSearch()"
              @keydown.enter.prevent="searchData()"
              @blur="searchData()"
            />
            <v-btn depressed color="grey" @click="searchData()">
              <v-icon>{{ icons.mdiMagnify }}</v-icon>
              Search
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card-title>
    <v-data-table
      class="admin-list-table"
      :headers="headers"
      :items="allTestators"
      :loading="loading || $apollo.loading"
      :options.sync="options"
      :server-items-length="totalTestators"
      :footer-props="{
        itemsPerPageOptions: [10, 25, 100, 500],
      }"
      @click:row="redirectToSummary"
    >
      <template #[`item.firstNames`]="{ item }">
        {{ item.firstNames }} {{ item.lastName }}
      </template>
      <template #[`item.createdAt`]="{ item }">
        {{ item.createdAt | date_DMMMYYYY }}
      </template>
      <template #[`item.lastLogin`]="{ item }">
        {{ item.lastLogin | date_DMMMYYYY }}
      </template>
      <template #[`item.nextBillingDate`]="{ item }">
        {{ item.nextBillingDate | date_DMMMYYYY }}
      </template>
      <template #[`item.paymentPlan`]="{ item }">
        {{ formatPaymentPlan(item.paymentPlan) }}
      </template>
      <template #[`item.estimatedEstateAdministrativeCosts`]="{ item }">
        <span v-if="item.estimatedEstateAdministrativeCosts">
          R{{ item.estimatedEstateAdministrativeCosts | currency }}
        </span>
      </template>
      <template #[`item.numberOfCompletedSteps`]="{ item }">
        <v-chip :color="stepsColor(item.numberOfCompletedSteps)" dark>
          {{ item.numberOfCompletedSteps }}
        </v-chip>
      </template>
      <template #[`item.lastWillDownloadDate`]="{ item }">
        {{ item.lastWillDownloadDate | date_DMMMYYYY }}
      </template>
      <template #[`item.flags`]="{ item }">
        <span
          class="v-chip theme--dark v-size--small red"
          v-if="item.deceased == 1"
        >
          Deceased
        </span>
        <span
          class="v-chip theme--dark v-size--small orange"
          v-if="item.doNotContact == 1"
        >
          Do not contact
        </span>
      </template>
      <template #[`item.uploadStatus`]="{ item }">
        <v-icon v-if="!!item.vaultStored" class="mr-1">{{
          icons.mdiSafeSquareOutline
        }}</v-icon>
        <v-icon
          :color="verificationStatusDetails(item.uploadStatus).color"
          dark
        >
          {{ verificationStatusDetails(item.uploadStatus).icon }}
        </v-icon>
      </template>
      <template #[`item.category`]="{ item }">
        <v-chip :color="categoryColor(item.category)" dark>
          {{ item.category }}
        </v-chip>
      </template>
    </v-data-table>

    <download-testator-list-csv
      :total-testators="totalTestators"
      :company="company"
    />
  </v-card>
</template>

<script>
  import { ALL_TESTATORS_QUERY } from '../../constants/graphql/testators';
  import { verificationStatus } from '../../constants/js/will-verification';
  import DownloadTestatorListCsv from './DownloadTestatorListCsv.vue';

  export default {
    name: 'TestatorsList',
    components: { DownloadTestatorListCsv },
    props: {
      company: {
        type: Object,
        default: () => {
          return {};
        },
      },
      searchAdvisorEmail: {
        type: String,
        default: null,
      },
    },
    data() {
      return {
        searchText: '',
        allTestators: [],
        loading: true,
        options: {},
        totalTestators: 0,
        searchQueryParam: '',
        categoriesFilter: null,
      };
    },
    computed: {
      headers() {
        var headers = [
          { text: 'Full name', value: 'firstNames', sortable: false },
          { text: 'Email', value: 'email' },
          { text: 'Invite / Signup Date', value: 'createdAt' },
          { text: 'Last login date', value: 'lastLogin' },
          { text: 'Payment plan', value: 'paymentPlan' },
          { text: 'Next billing date', value: 'nextBillingDate' },
          { text: 'Advisor', value: 'advisorEmail' },
          { text: 'Estate Costs', value: 'estimatedEstateAdministrativeCosts' },
          { text: 'Total steps completed', value: 'numberOfCompletedSteps' },
          { text: 'Last will download date', value: 'lastWillDownloadDate' },
          { text: 'Flags', value: 'flags' },
          { text: 'Will check', value: 'uploadStatus' },
        ];

        if (this.company.willPaymentOption == 'ALWAYS_FREE') {
          headers = headers.filter(
            header =>
              header.value != 'paymentPlan' && header.value != 'nextBillingDate'
          );
        }
        if (this.company.insuranceStep == 'NO_INSURANCE') {
          headers = headers.filter(
            header => header.value != 'estimatedEstateAdministrativeCosts'
          );
        }
        if (this.company.contactCenterWorkflow) {
          headers.push({ text: 'Contact center category', value: 'category' });
        }
        if (this.isAdvisor) {
          headers = headers.filter(header => header.value != 'advisorEmail');
        }

        return headers;
      },
      isAdvisor() {
        return this.$store.getters.isAdvisor;
      },
      categoriesList() {
        return [
          { text: 'Welcome', value: 'Welcome', color: 'blue' },
          {
            text: 'Educate and Assist',
            value: 'Educate and Assist',
            color: 'indigo',
          },
          {
            text: 'Requested a call',
            value: 'Requested a call',
            color: 'deep-orange',
          },
          {
            text: 'Signature Check',
            value: 'Signature Check',
            color: 'purple',
          },
          { text: 'Downloaded will', value: 'Downloaded will', color: 'teal' },
          {
            text: 'Full life-cycle Testator',
            value: 'Full life-cycle Testator',
            color: 'brown',
          },
          {
            text: 'Frequent Testator',
            value: 'Frequent Testator',
            color: 'green',
          },
          {
            text: 'Never logged in',
            value: 'Never logged in',
            color: 'blue-grey',
          },
        ];
      },
    },
    watch: {
      searchAdvisorEmail(newVal) {
        if (newVal) {
          this.searchText = newVal;
          this.searchData();
        }
      },
      options: {
        handler() {
          this.loading = true;
        },
        deep: true,
      },
      categoriesFilter() {
        this.options.page = 1;
      },
    },
    headers: [
      { text: 'Full name', value: 'firstNames', sortable: false },
      { text: 'Email', value: 'email' },
      { text: 'Invite / Signup Date', value: 'createdAt' },
      { text: 'Last login date', value: 'lastLogin' },
      { text: 'Payment plan', value: 'paymentPlan' },
      { text: 'Next billing date', value: 'nextBillingDate' },
      { text: 'Advisor', value: 'advisorEmail' },
      { text: 'Estate Costs', value: 'estimatedEstateAdministrativeCosts' },
      { text: 'Total steps completed', value: 'numberOfCompletedSteps' },
      { text: 'Last will download date', value: 'lastWillDownloadDate' },
      { text: 'Flags', value: 'flags' },
      { text: 'Will check', value: 'uploadStatus' },
      { text: 'Category', value: 'category' },
    ],
    methods: {
      redirectToSummary(item) {
        this.$router.push('advisors/will-summary/' + item.id);
      },
      stepsColor(numberOfCompletedSteps) {
        if (numberOfCompletedSteps > 4) return 'green';
        else if (numberOfCompletedSteps > 1) return 'orange';
        else return 'red';
      },
      categoryColor(category) {
        return this.categoriesList.find(obj => {
          return obj.text == category;
        })?.color;
      },
      verificationStatusDetails(status) {
        return verificationStatus(status, this.icons);
      },
      formatPaymentPlan(paymentPlan) {
        switch (paymentPlan) {
          case 'subscription':
            return 'Subscription';
          case 'once_off':
            return 'Once off';
          case 'free_company_will':
            return 'Free company will';
          default:
            return '';
        }
      },
      searchData() {
        this.searchQueryParam = this.searchText;
        this.options.page = 1;
      },
      clearSearch() {
        this.searchQueryParam = null;
        this.options.page = 1;
      },
      queryVariables() {
        const limit = this.options.itemsPerPage || 10;
        const page = this.options.page || 1;
        const search = this.searchQueryParam;
        const categories = this.categoriesFilter;
        let sortBy = null;
        let sortDesc = true;
        if (this.options.sortBy && this.options.sortBy.length) {
          sortBy = this.options.sortBy[0];
          sortDesc = this.options.sortDesc[0];
        }

        return { page, limit, search, sortBy, sortDesc, categories };
      },
    },
    apollo: {
      allTestators: {
        query: ALL_TESTATORS_QUERY,
        variables() {
          return this.queryVariables();
        },
        fetchPolicy: 'cache-and-network',
        result(response) {
          if (response.data) {
            this.totalTestators = response.data.allTestators.totalTestators;
            this.loading = false;
          }
        },
        update: data => data.allTestators.testators,
      },
    },
  };
</script>
